import type {
  TikTokCrawlJob,
  TikTokCreative,
  CreateTikTokCrawlRequest,
  ListCrawlJobsResponse,
  ListCreativesResponse,
} from './types'
import { API_CONFIG } from '@/shared/config'

const API_URL = API_CONFIG.baseUrl

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getAuthToken } = await import('@trafiflow/shared-ui')
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const tiktokIntelService = {
  /**
   * Create a new TikTok crawl job.
   */
  async createCrawlJob(request: CreateTikTokCrawlRequest): Promise<TikTokCrawlJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/tiktok-intel/jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create crawl job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List all TikTok crawl jobs for the current tenant.
   */
  async listCrawlJobs(): Promise<ListCrawlJobsResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/tiktok-intel/jobs`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to list crawl jobs: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a single crawl job with full stage status.
   */
  async getCrawlJob(jobId: string): Promise<TikTokCrawlJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/tiktok-intel/jobs/${jobId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get crawl job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List TikTok creatives, optionally filtered by job ID.
   */
  async listCreatives(jobId?: string): Promise<ListCreativesResponse> {
    const headers = await getAuthHeaders()
    const params = jobId ? `?job_id=${jobId}` : ''

    const response = await fetch(`${API_URL}/tiktok-intel/creatives${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to list creatives: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a single creative with enrichment data.
   */
  async getCreative(creativeId: string): Promise<TikTokCreative> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/tiktok-intel/creatives/${creativeId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get creative: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Re-run AI enrichment for a specific creative.
   */
  async reEnrichCreative(creativeId: string): Promise<TikTokCreative> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/tiktok-intel/creatives/${creativeId}/enrich`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to re-enrich creative: ${response.status}`)
    }

    return response.json()
  },
} as const
